import { useCallback, useEffect, useRef, useState } from 'react';
import type { UpdaterRun, UpdatesView } from '@teachpartner/contracts';
import { describeError, type HostAdminApi } from '../../api/client';

/** How often the page refreshes while the updater is busy. */
export const UPDATES_POLL_MS = 3000;

export interface UpdatesState {
  readonly view: UpdatesView | null;
  readonly error: string | null;
  /** Increments on every successful refresh (drives re-fetching a running run). */
  readonly revision: number;
  refresh(): Promise<void>;
}

/** Loads the updater view and polls it every UPDATES_POLL_MS while a run is in progress. */
export function useUpdates(api: HostAdminApi): UpdatesState {
  const [state, setState] = useState<Omit<UpdatesState, 'refresh'>>({
    view: null,
    error: null,
    revision: 0,
  });
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const view = await api.getUpdates();
      if (mounted.current) setState((s) => ({ view, error: null, revision: s.revision + 1 }));
    } catch (e) {
      if (mounted.current) setState((s) => ({ ...s, error: describeError(e) }));
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const busy = state.view?.state?.busy ?? false;
  useEffect(() => {
    if (!busy) return undefined;
    const timer = setInterval(() => void refresh(), UPDATES_POLL_MS);
    return () => clearInterval(timer);
  }, [busy, refresh]);

  return { ...state, refresh };
}

export interface RunDetails {
  readonly run: UpdaterRun | null;
  readonly error: string | null;
}

/**
 * Loads one run's steps and log. `refreshKey` changes when the run should be fetched again
 * (on every poll while it is running, and once more when it finishes).
 */
export function useRunDetails(
  api: HostAdminApi,
  runId: string | null,
  refreshKey: string,
): RunDetails {
  const [details, setDetails] = useState<RunDetails>({ run: null, error: null });
  useEffect(() => {
    if (!runId) return undefined;
    let active = true;
    api.getUpdateRun(runId).then(
      (run) => active && setDetails({ run, error: null }),
      (e: unknown) => active && setDetails((d) => ({ ...d, error: describeError(e) })),
    );
    return () => {
      active = false;
    };
  }, [api, runId, refreshKey]);
  const run = details.run && details.run.id === runId ? details.run : null;
  return { run, error: runId ? details.error : null };
}
